/**
 * ratingMoments: which inbox arrival counts as a milestone worth asking for a
 * rating on. Pure, so components/RatingMoments stays a thin effect and the
 * rules are unit-tested here (test/ratingMoments.test.tsx).
 *
 * A collection milestone (50 / 75 / 100% of a dex variant) is detected by the
 * Worker when a Pokédex scan lands and delivered in-app only, as a
 * `collection_milestone` inbox row the bell polls every minute (owner
 * direction 2026-09-11). So the client's celebration IS that row arriving:
 * the badge bumps, the panel shows the share card. The rating moment is ARMED
 * on the same arrival (lib/ratingDwell then waits for the trainer's look and
 * pause), with two guards:
 *
 *   - FIRST SIGHT IS A BASELINE. The first time this device sees the inbox for
 *     an account it only remembers the newest milestone id; a reinstall or a
 *     second phone must not celebrate months-old rows.
 *   - ONLY A FRESH ROW. A milestone row newer than the baseline fires only
 *     while it is under MILESTONE_FRESH_MS old; one that landed while the app
 *     was closed is still a milestone, but no longer a moment.
 */

/** A milestone row older than this is history, not a moment. */
export const MILESTONE_FRESH_MS = 15 * 60 * 1000;
/** The newest milestone id this device has seen, per account. */
export const MILESTONE_SEEN_KEY = "nx.rating.milestoneSeen.v1";

export interface InboxRowLike {
  id: number;
  kind: string;
  /** Epoch ms from the Worker; the Express dev server sends an ISO string. */
  createdAt: number | string;
}

export interface MilestoneSeen {
  accountId: number;
  id: number;
}

/** The Worker writes snake_case; the camelCase form is tolerated like the panel does. */
export function isMilestoneRow(kind: string): boolean {
  return kind === "collection_milestone" || kind === "collectionMilestone";
}

/**
 * ms epoch from a row's createdAt: epoch ms or seconds, or an ISO string.
 * NaN for anything else, which no freshness test passes.
 */
export function rowCreatedMs(createdAt: number | string): number {
  if (typeof createdAt === "string") return Date.parse(createdAt);
  if (!Number.isFinite(createdAt)) return Number.NaN;
  return createdAt < 1e12 ? createdAt * 1000 : createdAt;
}

export function readMilestoneSeen(): MilestoneSeen | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(MILESTONE_SEEN_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<MilestoneSeen>;
    if (typeof p.accountId !== "number" || typeof p.id !== "number") return null;
    return { accountId: p.accountId, id: p.id };
  } catch {
    return null;
  }
}

export function writeMilestoneSeen(seen: MilestoneSeen): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(MILESTONE_SEEN_KEY, JSON.stringify(seen));
  } catch {
    /* quota or private mode: at worst the baseline is taken again next time */
  }
}

/**
 * Given the inbox as fetched, and what this device remembers for this
 * account: the newest milestone id to remember, and whether a fresh milestone
 * arrived since the last look.
 */
export function pickMilestoneMoment(
  rows: readonly InboxRowLike[],
  seen: MilestoneSeen | null,
  accountId: number,
  now: number,
): { newestId: number; fire: boolean } {
  let newestId = 0;
  let fire = false;
  const baseline = seen != null && seen.accountId === accountId ? seen.id : null;
  for (const row of rows) {
    if (!isMilestoneRow(row.kind)) continue;
    if (row.id > newestId) newestId = row.id;
    if (baseline == null || row.id <= baseline) continue;
    if (now - rowCreatedMs(row.createdAt) <= MILESTONE_FRESH_MS) fire = true;
  }
  return { newestId, fire };
}
